/**
 * API Client for FogPilot
 *
 * Provides a typed fetch wrapper with error handling,
 * request/response interceptors, and retry logic.
 */

// In production, API_BASE_URL is empty (same origin). In dev, it points to the backend server.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  timeout?: number;
  params?: Record<string, string | number | boolean | undefined>;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: { message?: string; code?: string; details?: Record<string, unknown> } = {};

    try {
      errorData = await response.json();
    } catch {
      // Response is not JSON
    }

    throw new ApiError(
      errorData.message || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      errorData.code,
      errorData.details
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

function buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
  // Use current origin if API_BASE_URL is empty (production same-origin setup)
  const baseUrl = API_BASE_URL || window.location.origin;
  const url = new URL(endpoint, baseUrl);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { timeout = API_TIMEOUT, params, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const url = buildUrl(endpoint, params);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: 'include', // Include cookies for session auth
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    });

    return handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408, 'TIMEOUT');
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      0,
      'NETWORK_ERROR'
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * API client with typed methods for all endpoints
 */
export const api = {
  /**
   * GET request
   */
  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   */
  post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PUT request
   */
  put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'DELETE' });
  },
};

/**
 * API endpoints
 */
export const endpoints = {
  auth: {
    login: '/api/v1/auth/login',
    logout: '/api/v1/auth/logout',
    me: '/api/v1/auth/me',
  },
  devices: {
    list: '/api/v1/devices',
    get: (id: string) => `/api/v1/devices/${id}`,
    updateSite: (id: string) => `/api/v1/devices/${id}/site`,
  },
  sites: {
    list: '/api/v1/sites',
  },
  preview: {
    create: '/api/v1/preview',
    get: (id: string) => `/api/v1/preview/${id}`,
    approve: (id: string) => `/api/v1/preview/${id}/approve`,
  },
  tasks: {
    list: '/api/v1/tasks',
    get: (id: string) => `/api/v1/tasks/${id}`,
  },
  apply: '/api/v1/apply',
  fog: {
    health: '/api/v1/fog/health',
    dashboard: '/api/v1/fog/dashboard',
    systemInfo: '/api/v1/fog/system/info',
    hosts: '/api/v1/fog/hosts',
    host: (id: number) => `/api/v1/fog/hosts/${id}`,
    groups: '/api/v1/fog/groups',
    group: (id: number) => `/api/v1/fog/groups/${id}`,
    images: '/api/v1/fog/images',
    image: (id: number) => `/api/v1/fog/images/${id}`,
    tasks: '/api/v1/fog/tasks',
    task: (id: number) => `/api/v1/fog/tasks/${id}`,
    deploy: (hostId: number) => `/api/v1/fog/hosts/${hostId}/tasks/deploy`,
    capture: (hostId: number) => `/api/v1/fog/hosts/${hostId}/tasks/capture`,
  },
  remote: {
    statusSummary: '/api/v1/remote/status/summary',
    statusList: '/api/v1/remote/status',
    status: (deviceId: string) => `/api/v1/remote/status/${deviceId}`,
    checkStatus: (deviceId: string) => `/api/v1/remote/status/${deviceId}/check`,
    checkStatusBulk: '/api/v1/remote/status/check-bulk',
    checkStatusAll: '/api/v1/remote/status/check-all',
    wol: (deviceId: string) => `/api/v1/remote/wol/${deviceId}`,
    wolBulk: '/api/v1/remote/wol-bulk',
    wolRoom: (roomId: string) => `/api/v1/remote/wol-room/${roomId}`,
    wolGroup: (groupId: string) => `/api/v1/remote/wol-group/${groupId}`,
    powerLogs: '/api/v1/remote/power-logs',
    powerLogsDevice: (deviceId: string) => `/api/v1/remote/power-logs/${deviceId}`,
    credentials: (deviceId: string) => `/api/v1/remote/credentials/${deviceId}`,
    credentialsBulk: '/api/v1/remote/credentials/bulk',
  },
  scans: {
    scripts: '/api/v1/scans/scripts',
    script: (id: string) => `/api/v1/scans/scripts/${id}`,
    jobs: '/api/v1/scans/jobs',
    jobsRunning: '/api/v1/scans/jobs/running',
    job: (id: string) => `/api/v1/scans/jobs/${id}`,
    jobStart: (id: string) => `/api/v1/scans/jobs/${id}/start`,
    jobCancel: (id: string) => `/api/v1/scans/jobs/${id}/cancel`,
    jobResults: (id: string) => `/api/v1/scans/jobs/${id}/results`,
    run: '/api/v1/scans/run',
  },
  relay: {
    agents: '/api/relay/agents',
    createTask: '/api/relay/tasks/create',
    queue: '/api/relay/queue',
    cancelTask: (id: string) => `/api/relay/tasks/${id}/cancel`,
  },
  settings: {
    fog: '/api/v1/settings/fog',
    fogTest: '/api/v1/settings/fog/test',
  },
};
